import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button-tw";
import { MoreHorizontal, Pause, Play, SkipForward, XCircle } from "lucide-react";
import { invoiceQueries, invoiceQueryKeys } from "@/queries/invoice";
import { useAuth } from "@/providers/auth";
import { RecurringInvoiceSchedule } from "@/queries/invoice/types";
import { CancelSeriesDialog } from "./cancel-series-dialog";
import { SkipNextDialog } from "./skip-next-dialog";

interface RecurringActionsMenuProps {
  schedule: RecurringInvoiceSchedule;
}

export function RecurringActionsMenu({ schedule }: RecurringActionsMenuProps) {
  const queryClient = useQueryClient();
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const [cancelOpen, setCancelOpen] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);

  const pauseMutation = invoiceQueries.recurringInvoice.actions.pause.useMutation({
    onSuccess: () => {
      toast.success("Series paused.");
      queryClient.invalidateQueries(invoiceQueryKeys.recurringInvoice.list(userId));
    },
    onError: () => toast.error("Failed to pause series."),
  });

  const resumeMutation = invoiceQueries.recurringInvoice.actions.resume.useMutation({
    onSuccess: () => {
      toast.success("Series resumed.");
      queryClient.invalidateQueries(invoiceQueryKeys.recurringInvoice.list(userId));
    },
    onError: () => toast.error("Failed to resume series."),
  });

  const cancelMutation = invoiceQueries.recurringInvoice.actions.cancel.useMutation({
    onSuccess: () => {
      toast.success("Series cancelled.");
      setCancelOpen(false);
      queryClient.invalidateQueries(invoiceQueryKeys.recurringInvoice.list(userId));
    },
    onError: () => toast.error("Failed to cancel series."),
  });

  const skipMutation = invoiceQueries.recurringInvoice.actions.skipNext.useMutation({
    onSuccess: () => {
      toast.success("Next invoice skipped.");
      setSkipOpen(false);
      queryClient.invalidateQueries(invoiceQueryKeys.recurringInvoice.list(userId));
    },
    onError: () => toast.error("Failed to skip next invoice."),
  });

  const params = {
    userId,
    recurringInvoiceId: schedule.recurring_invoice_id,
  };

  const isActive = schedule.status === "active";
  const isPaused = schedule.status === "paused";
  const isCancelled = schedule.status === "cancelled";

  if (isCancelled) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {isActive && (
            <DropdownMenuItem
              onClick={() => pauseMutation.mutate(params)}
              disabled={pauseMutation.isLoading}
            >
              <Pause className="mr-2 h-4 w-4" />
              Pause
            </DropdownMenuItem>
          )}
          {isPaused && (
            <DropdownMenuItem
              onClick={() => resumeMutation.mutate(params)}
              disabled={resumeMutation.isLoading}
            >
              <Play className="mr-2 h-4 w-4" />
              Resume
            </DropdownMenuItem>
          )}
          {isActive && (
            <DropdownMenuItem
              onClick={() => setSkipOpen(true)}
            >
              <SkipForward className="mr-2 h-4 w-4" />
              Skip Next
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setCancelOpen(true)}
            className="text-red-600 focus:text-red-600"
          >
            <XCircle className="mr-2 h-4 w-4" />
            Cancel Series
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CancelSeriesDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onConfirm={() => cancelMutation.mutate(params)}
        isLoading={cancelMutation.isLoading}
      />
      <SkipNextDialog
        open={skipOpen}
        onOpenChange={setSkipOpen}
        onConfirm={() => skipMutation.mutate(params)}
        isLoading={skipMutation.isLoading}
      />
    </>
  );
}
